import { refusalOf } from '../utils/refusal';
import { API, graphqlOperation } from '../utils/amplifyCompat';
import * as custom_queries from '../customgraphql/queries';
import * as custom_mutations from '../customgraphql/mutations';
import * as customSubscriptions from '../customgraphql/subscriptions'
import { createChatRoomCreatedNotification } from './notification';
import { memberIdsOf, sameMembers } from '../utils/chatMemberIds';
import { graphqlGetUser, graphqlSkippingUnreadableRooms, isAuthorizationRefusal } from '../utils/getUserTolerant';

// Chat message subscription
// -----------------------------------------------------------------------------
export function chatCreateMessageSubscriptions(callback, _chatRoomId) {
  return async (dispatch, getState) => {
    const chatCreateSubscription = API.graphql(
      graphqlOperation(customSubscriptions.onCreateMessage)
    ).subscribe({
      next: (newChatData) => {
        const createdMessage = newChatData?.data?.onCreateMessage
        if (!createdMessage) {
          return
        }

        if (typeof callback === 'function') {
          callback(createdMessage)
        }

        const userData = getState()?.user?.userData
        // The scalar FK: since sprint PR 8b chatRoom is null in message events (v2 redaction).
        const roomId = createdMessage?.messageChatRoomId || createdMessage?.chatRoom?.id
        if (!userData || !userData.chatRoomUser || !Array.isArray(userData.chatRoomUser.items) || !roomId) {
          return
        }

        const roomIndex = userData.chatRoomUser.items.findIndex(
          (r) => r?.chatRoom?.id === roomId
        )

        if (roomIndex === -1) {
          return
        }

        const existingMessages = userData.chatRoomUser.items[roomIndex]?.chatRoom?.messages?.items || []
        const alreadyExists = existingMessages.some((m) => m.id === createdMessage.id)
        if (alreadyExists) {
          return
        }

        // Immutable update to ensure react-redux subscribers rerender.
        const nextUserData = {
          ...userData,
          chatRoomUser: {
            ...userData.chatRoomUser,
            items: userData.chatRoomUser.items.map((roomUser, index) => {
              if (index !== roomIndex) {
                return roomUser
              }

              return {
                ...roomUser,
                chatRoom: {
                  ...roomUser.chatRoom,
                  messages: {
                    ...roomUser.chatRoom.messages,
                    items: [createdMessage, ...existingMessages],
                  },
                },
              }
            }),
          },
        }

        dispatch({ type: 'GET_USER_DATA', userData: nextUserData })
      },
      error: (error) => {
        console.log('chatCreateMessageSubscriptions error: ', error)
      },
    })

    return chatCreateSubscription
  }
}

export function chatDeleteMessageSubscriptions(callback, _chatRoomId) {
  return async () => {
    const chatDeleteSubscription = API.graphql(
      graphqlOperation(customSubscriptions.onDeleteMessage)
    ).subscribe({
      next: (newChatData) => {
        const deletedMessage = newChatData?.data?.onDeleteMessage
        if (typeof callback === 'function' && deletedMessage) {
          callback(deletedMessage)
        }
      },
      error: (error) => {
        console.log('chatDeleteMessageSubscriptions error: ', error)
      },
    })

    return chatDeleteSubscription
  }
}

// Check existing chat rooms by user
// -----------------------------------------------------------------------------
// Returns the caller's rooms with exactly these members ({ items }, the oldest room first), or
// undefined if the query failed. Before the chat memberIds follow-up to sprint PR 8b anyone could
// create a ChatRoomUser row naming the caller with any `members` string, and a member could
// change a room's memberIds; such rows may still exist, so a row counts only if its room is
// readable, the room's memberIds are these members, and the row's own memberIds are the room's
// (a planted row lists its creator). A room without memberIds can't be read since PR 8b, so it
// never gets this far.
export function isRoomRowFor(row, members) {
  return !!row?.chatRoom && sameMembers(row.chatRoom.memberIds, memberIdsOf(members)) && sameMembers(row.memberIds, row.chatRoom.memberIds)
}

// Several matching rooms (e.g. an old duplicate): always the same one, the oldest.
const oldestFirst = (a, b) =>
  String(a.chatRoom.createdAt ?? '').localeCompare(String(b.chatRoom.createdAt ?? '')) || String(a.chatRoom.id).localeCompare(String(b.chatRoom.id))

export function checkExistingChatRoom(username, members) {
  return async () => {
    try {
      const data = await graphqlSkippingUnreadableRooms(
        graphqlOperation(custom_queries.doesChatRoomExist, { 
          chatRoomUserUserId: username, 
          members: { eq: members } }),
        ['doesChatRoomExist', 'items']
      );
      const found = data.data.doesChatRoomExist;
      return { ...found, items: (found?.items ?? []).filter((r) => isRoomRowFor(r, members)).sort(oldestFirst) };
    } catch (err) {
      console.log("error checkExistingChatRoom: ", err);
    }
  };
}

// Create chat room
// -----------------------------------------------------------------------------
export function createChatRoom(username, memberIds) {
  return async () => {
    try {
      const data = await API.graphql(
        graphqlOperation(custom_mutations.createChatRoom, { input: { 
          createdBy: username,
          receiverHasRead: false,
          memberIds,
        }})
      );
      return data.data.createChatRoom;
    } catch (err) {
      console.log("error create chat room: ", err);
      // A refusal (members only, or not allowed) is thrown so the page can say so
      // (containers/ChatRoom.js); any other failure still returns undefined.
      if (refusalOf(err)) throw err;
    }
  };
}

// Create message for chat
// -----------------------------------------------------------------------------
export function createChatMessage(username, chatRoomId, content, memberIds) {
  return async () => {
    try {
      const data = await API.graphql(
        graphqlOperation(custom_mutations.createMessage, { input: { 
          messageUserId: username,
          messageChatRoomId: chatRoomId, 
          content: content,
          memberIds,
        }})
      );
      return data.data.createMessage;
    } catch (err) {
      console.log("error createChatMessage: ", err);
      if (refusalOf(err)) throw err;
    }
  };
}

// Delete message for chat
// -----------------------------------------------------------------------------
export function deleteChatMessage(chatMessageId) {
  return async () => {
    try {
      const data = await API.graphql(
        graphqlOperation(custom_mutations.deleteMessage, { input: { 
          id: chatMessageId
        }})
      );
      return data.data.deleteMessage;
    } catch (err) {
      console.log("error deleteChatMessage: ", err);
    }
  };
}

// Create Chat Room Users - 3rd table that allows group chat.
// -----------------------------------------------------------------------------
export function createChatRoomUser(username, chatRoomId, members) {
  return async (dispatch, getState) => {
    try {
      const data = await API.graphql(
        graphqlOperation(custom_mutations.createChatRoomUser, { input: { 
          chatRoomUserUserId: username,
          chatRoomUserChatRoomId: chatRoomId, 
          members: members,
          memberIds: memberIdsOf(members),
        }})
      );

      // Send notification to users in the chat room except for the creator.
      if(username !== getState().user.userData.id){
        createChatRoomCreatedNotification(username, chatRoomId, getState().user.userData)
      }
      return data.data.createChatRoomUser;
    } catch (err) {
      console.log("error createChatRoomUser: ", err);
    }
  };
}

// Remove Chat Room Users - User leaves a chat room
// -----------------------------------------------------------------------------
export function deleteChatRoomUser(chatRoomId, username) {
  return async (dispatch, getState) => {
    try {
      const getChatRoomUserId = await API.graphql(
        graphqlOperation(custom_queries.chatRoomUserChatRoomIdAsKey, {
          chatRoomUserChatRoomId: chatRoomId, 
          chatRoomUserUserId: {eq: username }
        })
      )

      if(getChatRoomUserId.data.chatRoomUserChatRoomIdAsKey.items.length > 0) {
        const data = await API.graphql(
          graphqlOperation(custom_mutations.deleteChatRoomUser, { input: { 
            id: getChatRoomUserId.data.chatRoomUserChatRoomIdAsKey.items[0].id
          }})
        );
        getState().user.userData.chatRoomUser.items = getState().user.userData.chatRoomUser.items.filter( cr => cr.id !== getChatRoomUserId.data.chatRoomUserChatRoomIdAsKey.items[0].id)
        dispatch({ type: "GET_USER_DATA", userData: getState().user.userData })
        return data.data.deleteChatRoomUser;
      } else {
        console.log("Can't remove chat room user data.")
        return null
      }

    } catch (err) {
      console.log("error deleteChatRoomUser: ", err);
    }
  };
}


// Update Chat Room Users - to update updatedAt when there's new message in a chat room.
// -----------------------------------------------------------------------------
export function updateChatRoomUser(chatRoomId, updatedTime) {
  return async () => {
    try {
      const data = await API.graphql(
        graphqlOperation(custom_mutations.updateChatRoomUser, { input: { 
          id: chatRoomId,
          updatedAt: updatedTime
        }})
      );
      return data.data.updateChatRoomUser;
    } catch (err) {
      console.log("error updateChatRoomUser: ", err);
    }
  };
}


// Get ChatRoomUser by chat room ID
// -----------------------------------------------------------------------------
export function getChatRoomUsersByChatRoomId(chatRoomId) {
  return async () => {
    try {
      const data = await API.graphql(
        graphqlOperation(custom_queries.chatRoomUserChatRoomIdAsKey, {
          chatRoomUserChatRoomId: chatRoomId
        })
      )
      return data.data.chatRoomUserChatRoomIdAsKey;
    } catch (err) {
      console.log("error getting getChatRoomUsersByChatRoomId: ", err);
    }
  };
}

// Get chat room data
// -----------------------------------------------------------------------------
export function getChatRoomData(chatRoomId, limit, token) {
  return async () => {
    try {
      const data = await API.graphql(
        graphqlOperation(custom_queries.getChatRoom, {
          id: chatRoomId,
          limit: limit,
          nextTokenMessage: token
        })
      )
      return data.data.getChatRoom;
    } catch (err) {
      console.log("error getting getChatRoomData: ", err);
      // Since sprint PR 8b a room the user isn't a member of is refused: "No chat found here"
      // (null), as for a room that doesn't exist. Any other error (network, throttling) is
      // thrown, so the page doesn't claim the room is gone.
      if (isAuthorizationRefusal(err)) return null;
      throw err;
    }
  };
}

// Update receiverHasRead in ChatRoom 
// -----------------------------------------------------------------------------
export function updateChatRoomRead(chatRoomId, read) {
  return async (dispatch, getState) => {
    try {
      const data = await API.graphql(
        graphqlOperation(custom_mutations.updateChatRoom, { input: { 
          id: chatRoomId, 
          receiverHasRead: read
        }})
      );

      // Update receiverHasRead value in local redux for chat room list
      if(read){
        const room = getState().user.userData?.chatRoomUser?.items?.find((r) => r?.chatRoom?.id === data.data.updateChatRoom.id)
        if (room) {
          room.chatRoom.receiverHasRead = data.data.updateChatRoom.receiverHasRead
          dispatch({ type: "GET_USER_DATA", userData: getState().user.userData })
        }
      }
      return data.data.updateChatRoom;
    } catch (err) {
      console.log("error updateChatRoom: ", err);
    }
  };
}

// Get More ChatRoomUsers
// -----------------------------------------------------------------------------
export function getMoreChatRoomUsers(username, nextTokenChatRoomUser) {
  return async (dispatch, getState) => {
    try { 
      const data = await graphqlGetUser(graphqlOperation(custom_queries.getUser, {id: username, nextTokenChatRoomUser: nextTokenChatRoomUser}))
      getState().user.userData.chatRoomUser.items = [...getState().user.userData.chatRoomUser.items, ...data.data.getUser.chatRoomUser.items]
      getState().user.userData.chatRoomUser.nextToken = data.data.getUser.chatRoomUser.nextToken
      dispatch({ type: "GET_USER_DATA", userData: getState().user.userData })
    } catch(err) {
      console.log('error getMoreChatRoomUsers: ', err)
    }
  }
}

